/**
 * `dashboards.layouts` (lkAQhQYtVPMt): which dashboard each role lands on.
 *
 *   by_role   role id → layout id. A role that is not in it gets the built-in layout of its name
 *             (DASHBOARD_LAYOUT_BY_ROLE below: Broker → broker, Brokerage Assistant →
 *             brokerage_assistant, every other role → default), so the empty
 *             default changes nobody's dashboard. The layout decides which widget slots exist; the
 *             widget:* grants still decide which of them render.
 *   designs   role id → the role's own design (Dashboard designer step 2): rows of columns of cards
 *             (DashboardDesign below; a value saved in the old zoned shape is read as rows,
 *             designFromZones). A role with a design gets it; every other
 *             role gets the template of its layout (DASHBOARD_TEMPLATES below, the built-in layouts as
 *             data), so the empty default changes nobody's dashboard either. Grants still decide which
 *             placed cards render. A design may carry options per placed card (`settings`: a time window, me or
 *             my team), for the cards that offer them.
 *
 * Who: editPermission feature:settings_permissions (the Roles tab's key; Super Admin and AIG staff
 * always pass). No readPermission: every signed-in user reads the map through
 * GET /api/settings/dashboard-layouts, the dashboard picks its layout from it.
 * Edited on Settings → Security & Permissions → Role Definitions (the role form's Dashboard picker),
 * not on App settings (editedOn).
 */
import { z } from 'zod';
import { BROKER_ROLE, BROKERAGE_ASSISTANT_ROLE } from '../../permissions/roles.js';
import { DASHBOARD_CARD_KEYS } from '../../permissions/widget-catalog.js';
import { defineGroup } from '../define.js';
/** Every dashboard layout; each has a template in DASHBOARD_TEMPLATES below (tsc checks it). */
export const DASHBOARD_LAYOUT_IDS = ['broker', 'brokerage_assistant', 'default'];
/**
 * The built-in layout of a role name (`roles.name`, the user's primary role), used when `by_role` has no entry for
 * the role; every role not here gets 'default'. Read by the web dashboard (widgets/dashboardLayouts.ts) and the
 * role matrix (scripts/preview-workflow/role-matrix.mjs).
 */
export const DASHBOARD_LAYOUT_BY_ROLE = {
    [BROKER_ROLE]: 'broker',
    [BROKERAGE_ASSISTANT_ROLE]: 'brokerage_assistant',
};
/** At most this many columns side by side in one row. */
export const DASHBOARD_MAX_COLUMNS = 3;
/** The time windows a card can be set to, in days. */
export const DASHBOARD_WINDOW_DAYS = [7, 30, 90, 365];
/** Whose work a card counts: the viewer's own, or their team's. */
export const DASHBOARD_CARD_SCOPES = ['me', 'team'];
/** Every card a design places, in reading order: row by row, column by column, top to bottom. */
export function designCards(design) {
    return design.rows.flatMap((r) => r.columns.flatMap((c) => c.cards));
}
const lone = (cards) => ({ columns: [{ cards }] });
/**
 * An old zoned design as rows, drawn the same: each `top` card its own row; then `main` beside `side` as one row of a
 * two-thirds column and a one-third column. With only one of the two, that one is a single full-width column (as the
 * old renderer drew a `main` with no `side`; a `side` with no `main` used to sit one third wide beside nothing).
 */
export function designFromZones({ top, main, side }) {
    const rows = top.map((key) => lone([key]));
    if (main.length > 0 && side.length > 0)
        rows.push({ columns: [{ cards: main, span: 2 }, { cards: side }] });
    else if (main.length > 0 || side.length > 0)
        rows.push(lone(main.length > 0 ? main : side));
    return { rows };
}
/**
 * Each built-in layout as a design, transcribed from the layout components it replaced (git history: DashboardPage's
 * DefaultDashboard, components/dashboard/BrokerDashboard.tsx and BrokerageAssistantDashboard.tsx), and equal to
 * designFromZones of the zoned templates that came before rows (tests/settings.test.ts). The web renders every
 * dashboard from one of these unless the role has a saved design.
 */
export const DASHBOARD_TEMPLATES = {
    default: {
        rows: [lone(['default_hero']), lone(['default_kpi_strip']), lone(['my_listing_tasks']), lone(['pinned_properties', 'shared_with_me', 'research_queue'])],
    },
    broker: {
        rows: [
            lone(['broker_hero']),
            lone(['broker_metric_strip']),
            { columns: [{ cards: ['my_requests'], span: 2 }, { cards: ['pinned_properties', 'shared_with_me', 'my_submissions'] }] },
        ],
    },
    brokerage_assistant: {
        rows: [{ columns: [{ cards: ['my_listing_tasks', 'my_listings'], span: 2 }, { cards: ['team_workload', 'shared_with_me', 'pinned_properties'] }] }],
    },
};
const widgetKey = z.enum(DASHBOARD_CARD_KEYS);
const onceEach = (keys) => new Set(keys).size === keys.length;
const ONCE = { message: 'A card can be placed only once' };
const columnSchema = z
    .object({
    cards: z.array(widgetKey).min(1, 'A column needs at least one card').readonly(),
    span: z.union([z.literal(1), z.literal(2)]).optional(),
})
    .strict();
const rowSchema = z
    .object({
    columns: z
        .array(columnSchema)
        .min(1, 'A row needs at least one column')
        .max(DASHBOARD_MAX_COLUMNS, `A row has at most ${DASHBOARD_MAX_COLUMNS} columns`)
        .readonly(),
})
    .strict()
    .refine((r) => r.columns.length !== 1 || (r.columns[0].span ?? 1) === 1, { message: "A row's only column is full width (no span)" })
    .refine((r) => r.columns.reduce((n, c) => n + (c.span ?? 1), 0) <= DASHBOARD_MAX_COLUMNS, {
    message: `A row's columns span at most ${DASHBOARD_MAX_COLUMNS}`,
});
const cardSettingsSchema = z
    .object({
    windowDays: z
        .custom((v) => DASHBOARD_WINDOW_DAYS.includes(v), {
        message: `A time window is one of ${DASHBOARD_WINDOW_DAYS.join(', ')} days`,
    })
        .optional(),
    scope: z.enum(DASHBOARD_CARD_SCOPES).optional(),
})
    .strict();
/**
 * One role's design: rows of 1–3 non-empty columns, dashboard cards only, each placed once, and options for placed
 * cards only. What a save sends.
 */
export const dashboardDesignSchema = z
    .object({ rows: z.array(rowSchema).readonly(), settings: z.record(widgetKey, cardSettingsSchema).optional() })
    .strict()
    .refine((d) => onceEach(designCards(d)), ONCE)
    .refine((d) => {
    const placed = new Set(designCards(d));
    return Object.keys(d.settings ?? {}).every((k) => placed.has(k));
}, { message: 'Settings are for placed cards only' });
const zone = z.array(widgetKey).readonly();
/** The zoned shape (LegacyDashboardDesign), read only to convert it. */
export const legacyDashboardDesignSchema = z
    .object({ top: zone, main: zone, side: zone })
    .strict()
    .refine((d) => onceEach([...d.top, ...d.main, ...d.side]), ONCE);
/** A stored design: rows, or the zoned shape converted to rows (designFromZones). Reads and saves see rows only. */
export const storedDashboardDesignSchema = z.union([
    dashboardDesignSchema.transform((d) => d),
    legacyDashboardDesignSchema.transform((d) => designFromZones(d)),
]);
export const dashboardsLayouts = defineGroup('dashboards.layouts', {
    label: 'Dashboard per role',
    editPermission: 'feature:settings_permissions',
    editedOn: 'Settings → Security & Permissions → Role Definitions',
    settings: {
        by_role: {
            type: z.record(z.string().uuid(), z.enum(DASHBOARD_LAYOUT_IDS)),
            default: {},
            label: 'Dashboard layout per role',
            help: 'A role not listed gets its built-in dashboard: Broker and Brokerage Assistant their own, every other role the default one.',
        },
        designs: {
            type: z.record(z.string().uuid(), storedDashboardDesignSchema),
            default: {},
            label: 'Dashboard design per role',
            help: "A role not listed gets its layout's built-in arrangement of cards.",
        },
    },
});
//# sourceMappingURL=dashboards.js.map